import { useState, useRef, useEffect } from "react";
import { Camera, Tag, X, Soup, Utensils, Coffee, Leaf, Candy } from "lucide-react";
import { Button, Modal, Select } from "../../../components";
import axios from "axios";
import { GET_MENU_ITEMS_URL, GET_TAGS_URL } from "../../../features/UrlHelper";
import { ALLERGENS } from "../../../features/orderConstants";

// ─── Static constants ──────────────────────────────────────────────────────────

const ITEM_TYPES = [
  { value: "SOUP",      label: "Polievka",     Icon: Soup },
  { value: "MENU",      label: "Hlavné jedlo", Icon: Utensils },
  { value: "DRINK",     label: "Nápoj",        Icon: Coffee },
  { value: "SIDE_DISH", label: "Príloha",      Icon: Leaf },
  { value: "DESSERT",   label: "Dezert",       Icon: Candy },
];

const itemTypeOptions = ITEM_TYPES.map(({ value, label, Icon }) => ({
  value,
  label: (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon size={15} />
      {label}
    </span>
  ),
}));

const EMPTY_FORM = {
  name: "",
  description: "",
  item_type: "MENU",
  allergens: [],
  photo: null,
  photoName: "",
  tags: [],
};


// ─── CreateMealModal ───────────────────────────────────────────────────────────

function CreateMealModal({ isOpen, onClose, onMealCreated, topTags = [], initialCategory = null }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoHovered, setPhotoHovered] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...EMPTY_FORM, item_type: initialCategory || EMPTY_FORM.item_type });
      setPhotoHovered(false);
      setTagInput("");
      setTagSuggestions([]);
      setShowSuggestions(false);
      setIsSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen, initialCategory]);

  function handleClose() {
    onClose();
  }

  const clearPhoto = () => {
    setForm((prev) => ({ ...prev, photo: null, photoName: "" }));
    setPhotoHovered(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setForm((prev) => ({ ...prev, photo: ev.target.result, photoName: file.name }));
    reader.readAsDataURL(file);
  };

  const toggleAllergen = (id) => {
    setForm((prev) => ({
      ...prev,
      allergens: prev.allergens.includes(id)
        ? prev.allergens.filter((a) => a !== id)
        : [...prev.allergens, id].sort((a, b) => a - b),
    }));
  };

  const handleTagInputChange = (e) => {
    const val = e.target.value;
    setTagInput(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setTagSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(GET_TAGS_URL(val.trim()));
        setTagSuggestions(data);
        setShowSuggestions(true);
      } catch {
        // ignore
      }
    }, 250);
  };

  const addTag = (tag) => {
    if (form.tags.some((t) => t.name.toLowerCase() === tag.name.toLowerCase())) return;
    setForm((prev) => ({ ...prev, tags: [...prev.tags, { id: tag.id ?? null, name: tag.name }] }));
    setTagInput("");
    setTagSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (name) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t.name !== name) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (!trimmed) return;
      const matched = tagSuggestions.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
      addTag(matched ?? { id: null, name: trimmed });
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const data = {
      name: form.name,
      description: form.description,
      item_type: form.item_type,
      allergens_list: form.allergens.length > 0 ? form.allergens.join(",") : null,
      tags: form.tags,
      ...(form.photo !== null ? { photo: form.photo } : {}),
    };

    try {
      const response = await axios.put(GET_MENU_ITEMS_URL, data);
      if (onMealCreated) {
        onMealCreated(response.data);
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Failed to create meal:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Pridať nové jedlo"
      className="admin__modal--wide"
    >
      <form className="admin__form" onSubmit={handleSubmit}>
        <div className="admin__form-meal-grid">

          {/* ── Left column ── */}
          <div className="admin__form-meal-col">
            <div className="admin__form-field">
              <Select
                label="Typ jedla"
                options={itemTypeOptions}
                value={form.item_type}
                onChange={(val) => setForm((prev) => ({ ...prev, item_type: val }))}
              />
            </div>

            <div className="admin__form-field">
              <label className="admin__form-label">Názov</label>
              <input
                maxLength={100}
                className="admin__form-input"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="napr. Cesnaková polievka"
                required
              />
            </div>

            <div className="admin__form-field">
              <label className="admin__form-label">Popis</label>
              <textarea
                maxLength={255}
                className="admin__form-input"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Krátky popis jedla"
                rows={4}
                style={{ resize: "vertical", height: "auto", minHeight: 90 }}
              />
            </div>

            <div className="admin__form-field">
              <label className="admin__form-label">Fotografia</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera size={14} />
                  {form.photoName ? form.photoName : "Vybrať súbor"}
                </Button>
                {form.photo && (
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                    onMouseEnter={() => setPhotoHovered(true)}
                    onMouseLeave={() => setPhotoHovered(false)}
                  >
                    <img
                      src={form.photo}
                      alt="náhľad"
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid var(--c-neutral-300)",
                        cursor: "zoom-in",
                        display: "block",
                      }}
                    />
                    {photoHovered && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "calc(100% + 10px)",
                          left: "50%",
                          transform: "translateX(-50%)",
                          zIndex: 1000,
                          background: "var(--c-neutral-100)",
                          borderRadius: 10,
                          boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
                          border: "1px solid var(--c-neutral-300)",
                          overflow: "hidden",
                          pointerEvents: "none",
                        }}
                      >
                        <img
                          src={form.photo}
                          alt="náhľad"
                          style={{ width: 220, height: 220, objectFit: "contain", display: "block" }}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={clearPhoto}
                      title="Zmazať fotku"
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        width: 18,
                        height: 18,
                        background: "var(--c-error)",
                        color: "var(--c-on-accent)",
                        border: "none",
                        borderRadius: "50%",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* ── Right column ── */}
          <div className="admin__form-meal-col">

            {/* Tags */}
            <div className="admin__form-field">
              <label className="admin__form-label">
                <Tag size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />
                Tagy
              </label>

              {form.tags.length > 0 && (
                <div className="admin__tag-list">
                  {form.tags.map((tag) => (
                    <span key={tag.name} className="admin__tag admin__tag--selected">
                      {tag.name}
                      <button
                        type="button"
                        className="admin__tag-remove"
                        onClick={() => removeTag(tag.name)}
                        aria-label={`Odstrániť tag ${tag.name}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="admin__tag-input-wrapper">
                <input
                  className="admin__form-input"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagKeyDown}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  onFocus={() => tagSuggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Napíš tag a stlač Enter..."
                  autoComplete="off"
                />
                {showSuggestions && tagSuggestions.length > 0 && (
                  <div className="admin__tag-suggestions">
                    {tagSuggestions.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className="admin__tag-suggestion-item"
                        onMouseDown={() => addTag(tag)}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {topTags.length > 0 && (
                <div className="admin__tag-popular">
                  <span className="admin__tag-popular-label">Populárne:</span>
                  {topTags.map((tag) => {
                    const isSelected = form.tags.some(
                      (t) => t.name.toLowerCase() === tag.name.toLowerCase()
                    );
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        className={`admin__tag ${isSelected ? "admin__tag--selected" : "admin__tag--popular"}`}
                        onClick={() => !isSelected && addTag(tag)}
                        disabled={isSelected}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Allergens */}
            <div className="admin__form-field">
              <label className="admin__form-label">Alergény</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px 20px",
                  maxHeight: 260,
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                {ALLERGENS.map((a) => (
                  <label
                    key={a.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 7,
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1.4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.allergens.includes(a.id)}
                      onChange={() => toggleAllergen(a.id)}
                      style={{ marginTop: 2, accentColor: "var(--c-primary)", flexShrink: 0 }}
                    />
                    <span>
                      <strong>{a.id}.</strong> {a.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="admin__form-actions">
          <Button variant="ghost" type="button" onClick={handleClose} disabled={isSubmitting}>
            Zrušiť
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Ukladám..." : "Pridať jedlo"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateMealModal;
